import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { OnboardingStatus } from '../../core/services/onboarding.service';
import { UserStateService } from '../../core/services/user-state.service';
import { LoggingService } from '../../core/services/logging.service';
import { switchMap, EMPTY, Subject, takeUntil } from 'rxjs';

/**
 * Resolving Component
 *
 * Centralized onboarding resolution page that:
 * 1. Shows a clean loading screen (no dashboard UI)
 * 2. Fetches onboarding status once
 * 3. Redirects to appropriate page based on status
 * 4. Eliminates route flashing during onboarding
 *
 * This component is the Auth0 callback destination and the central
 * routing hub for all onboarding-related navigation.
 */
@Component({
  selector: 'app-resolving',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './resolving.component.html',
  styleUrls: ['./resolving.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResolvingComponent implements OnInit, OnDestroy {
  statusMessage = 'Loading your account...';
  private targetUrl: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private auth: AuthService,
    private router: Router,
    private userStateService: UserStateService,
    private logger: LoggingService
  ) {
    // Access navigation state in constructor as it's not available in ngOnInit
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state?.['target']) {
      this.logger.debug('[Resolving] Target URL from navigation state:', navigation.extras.state['target']);
      this.targetUrl = navigation.extras.state['target'];
    }
  }

  ngOnInit() {
    // Check Auth0 appState (this will override navigation state if present)
    this.auth.appState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((appState) => {
      if (appState && appState.target) {
        this.targetUrl = appState.target;
      }
    });

    // Wait for Auth0 to be ready, then resolve onboarding
    this.auth.isAuthenticated$.pipe(
      takeUntil(this.destroy$),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          // Not authenticated, redirect to login with target URL if available
          this.logger.info('[Resolving] User not authenticated, redirecting to login');
          // Only pass targetUrl if it exists and is not the resolving page itself
          if (this.targetUrl && this.targetUrl !== '/resolving') {
            this.auth.loginWithRedirect({
              appState: { target: this.targetUrl }
            });
          } else {
            // No specific target, just redirect to login without appState
            this.auth.loginWithRedirect();
          }
          return EMPTY;
        }

        // Get state (will fetch from API if not initialized)
        return this.userStateService.getOrFetchState();
      })
    ).subscribe({
      next: (status) => {
        this.resolveDestination(status);
      },
      error: (error) => {
          this.logger.error('[Resolving] Error resolving onboarding:', error);

        // Handle 404 (no onboarding record) - new user
        if (error.status === 404) {
          this.logger.info('[Resolving] No onboarding record found (404), redirecting to email verification');
          this.statusMessage = 'Setting up your account...';
          setTimeout(() => {
            this.router.navigate(['/sign-up/verify-email']);
          }, 500);
        } else {
          // Other errors - go to error page
          this.logger.error('[Resolving] Unexpected error, redirecting to error page');
          this.router.navigate(['/error']);
        }
      }
    });
  }

  /**
   * Resolve the destination based on onboarding status.
   * Full onboarding flow (in order):
   * 1. Email verification
   * 2. Subscription selection
   * 3. Terms acceptance
   * 4. Profile completion (first and last name)
   * 5. Dashboard access
   */
  private resolveDestination(status: OnboardingStatus) {
    // Step 1: Email verification
    if (!status.emailVerified) {
      this.logger.info('[Resolving] Email not verified, redirecting to verify-email');
      this.statusMessage = 'Checking email verification...';
      setTimeout(() => {
        this.router.navigate(['/sign-up/verify-email']);
      }, 500);
      return;
    }

     // Step 2: Terms of Service and Privacy Policy acceptance
    if (!status.termsAccepted) {
      this.logger.info('[Resolving] Terms not accepted, redirecting to accept-terms');
      this.statusMessage = 'Loading terms of service...';
      setTimeout(() => {
        this.router.navigate(['/accept-terms']);
      }, 500);
      return;
    }

    // Step 3: Subscription — user must have chosen a plan to access the app.
    // 'new' is the initial status for users who have not yet selected any subscription plan.
    // 'free' (and above) means the user has made a deliberate subscription choice.
    if (status.accountStatus === 'new') {
      this.logger.info('[Resolving] No subscription selected, redirecting to pricing');
      this.statusMessage = 'Loading subscription options...';
      setTimeout(() => {
        this.router.navigate(['/pricing']);
      }, 500);
      return;
    }

    // Step 4: Profile completion — first and last name are required
    if (!status.profileComplete) {
      this.logger.info('[Resolving] Profile incomplete, redirecting to complete-profile');
      this.statusMessage = 'Setting up your profile...';
      setTimeout(() => {
        this.router.navigate(['/complete-profile']);
      }, 500);
      return;
    }

    // All required steps complete, go to dashboard or saved target URL
    this.statusMessage = 'Loading dashboard...';
    setTimeout(() => {
      this.router.navigate([this.targetUrl || '/dashboard']);
    }, 500);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
